/* ════════════════════════════════════════════════════════════
   FLIP cover→hero transition (two-phase).

   Exposes:
     window.__csFlipOpen(slug, sourceImg)  — portfolio → case study
     window.__csFlipClose(slug)            — case study → portfolio

   Two-phase open:
     Phase 1 (500ms) — clone of clicked cover flies on top of the
                       still-visible portfolio to the hero rect;
                       other cards slide down 600px and fade.
     Phase 2 (300ms) — case-study view is swapped in (display:''
                       + scrollTop set sync), real hero is hidden
                       under the clone, then content fades in and
                       the clone is removed.

     The case-study DOM is display:none for the entire duration
     of Phase 1, so the user never sees the scroller paint at the
     wrong scroll position.

   Close (for now) — simple opacity crossfade. Reverse FLIP TBD.
   ════════════════════════════════════════════════════════════ */
'use strict';

const portfolio = document.getElementById('view-portfolio');
const caseStudy = document.getElementById('view-case-study');
/* Top-level early-return isn't allowed in modules, so wrap the rest
   in a guard. Same semantic as the original IIFE's bail-out. */
if (portfolio && caseStudy) {

  const FLIP_MS = 500;
  const FLIP_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
  const FADE_MS  = 300;
  const CLOSE_MS = 250;

  /* Mobile viewport detection — checked at call time, not module load,
     because viewport can change (orientation, devtools resize). The
     <=768 threshold matches the existing case-study mobile breakpoint
     in src/case-study.js / public/index.html. */
  function isMobile() {
    return window.innerWidth <= 768;
  }

  /* On mobile, the simultaneous mount of #view-portfolio AND
     #view-case-study during FLIP Phase 2, combined with per-theme
     WebGL backgrounds (light-rays, PixelBlast, ColorBends, tunnel)
     still running, pushes Safari over the iOS WebContent memory
     limit and causes the page to be process-killed mid-FLIP — the
     OS then reloads /work/<slug> as a "recovery" and the preloader
     paints. Tearing the heavy stuff down at the start of the FLIP
     keeps peak memory under the iOS limit. Desktop has no such cap
     so we keep the safety-net behaviour there. */
  function stopHeavyBackgrounds() {
    try { if (window.__lightRaysStop) window.__lightRaysStop(); } catch (e) {}
    try { if (window.__blastStop)     window.__blastStop();     } catch (e) {}
    try { if (window.__bendsStop)     window.__bendsStop();     } catch (e) {}
    try { if (window.__tunnelStop)    window.__tunnelStop();    } catch (e) {}
    try { if (window.__bgStop)        window.__bgStop();        } catch (e) {}
  }

  function sleep(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }
  function nextPaint() {
    return new Promise(function (resolve) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { resolve(); });
      });
    });
  }
  function onceTransitionEnd(el, propertyName, fallbackMs) {
    return new Promise(function (resolve) {
      let done = false;
      function finish() {
        if (done) return;
        done = true;
        el.removeEventListener('transitionend', handler);
        resolve();
      }
      function handler(e) {
        if (e.target !== el) return;
        if (propertyName && e.propertyName !== propertyName) return;
        finish();
      }
      el.addEventListener('transitionend', handler);
      setTimeout(finish, fallbackMs);
    });
  }

  function getHeroImg(slug) {
    const sec = caseStudy.querySelector('.cs-section[data-slug="' + slug + '"]');
    return sec ? sec.querySelector('.cs-hero img') : null;
  }

  /* Compute the destination rect — where the clone needs to land
     to look like the hero in the case study. The hero <img> isn't
     laid out yet (case study is display:none in Phase 1), so we
     compute the rect from CSS values that match the case-study
     layout: max-width 1100px content, padding 32px top, centered
     horizontally. Height matches the
     hero's CSS aspect-ratio (16/9) so when the clone is removed
     and the real hero takes over there's no visible size snap.
     The clone's <img> uses object-fit:cover so any difference
     between the cover's intrinsic aspect and 16:9 just crops the
     image rather than deforming it. */
  const HERO_ASPECT_W = 16;
  const HERO_ASPECT_H = 9;
  function computeHeroDstRect(/* srcRect */) {
    const vw = window.innerWidth;
    const narrow = vw <= 720;
    const pad    = narrow ? 16 : 24;
    /* Mirror .cs-section padding-top exactly so the FLIP clone lands
       in the hero's final resting position with no post-FLIP jump.
       Desktop: 96px (clears the 80px top blur band). Mobile (≤720):
       72px (clears the 32px top blur band + breathing room). */
    const padTop = narrow ? 72 : 96;
    const width  = Math.min(1100, vw - pad * 2);
    const height = width * (HERO_ASPECT_H / HERO_ASPECT_W);
    const left   = (vw - width) / 2;
    return { top: padTop, left: left, width: width, height: height };
  }

  /* Build the flying <img> clone. It's a fresh <img> with the same
     src as the cover (browser reuses cached pixels), positioned in
     fixed coordinates over the source. All movement is via top/
     left/width/height transitions so the rendered size = the
     intrinsic layout size at every frame (no transform scaling
     artifacts at large deltas). */
  function buildClone(srcImg, srcRect) {
    const clone = document.createElement('img');
    clone.src = srcImg.currentSrc || srcImg.src;
    clone.alt = '';
    clone.className = 'cs-flip-clone';
    clone.style.top         = srcRect.top + 'px';
    clone.style.left        = srcRect.left + 'px';
    clone.style.width       = srcRect.width + 'px';
    clone.style.height      = srcRect.height + 'px';
    clone.style.borderRadius = '20px';
    return clone;
  }

  /* Mirror of buildClone for the close path — clones the hero
     image instead of a cover. Kept separate from buildClone for
     documentation, but the structure is identical. */
  function buildCloneFromHero(heroImg, rect) {
    const clone = document.createElement('img');
    clone.src = heroImg.currentSrc || heroImg.src;
    clone.alt = '';
    clone.className = 'cs-flip-clone';
    clone.style.top         = rect.top + 'px';
    clone.style.left        = rect.left + 'px';
    clone.style.width       = rect.width + 'px';
    clone.style.height      = rect.height + 'px';
    clone.style.borderRadius = '20px';
    return clone;
  }

  /* Lookups shared by open and close paths. */
  function getCoverWrapperBySlug(slug) {
    const a = portfolio.querySelector('.cover-item[href="/work/' + slug + '"]');
    return a ? a.closest('.cover-wrapper') : null;
  }
  function getCoverImg(wrapper) {
    return wrapper ? wrapper.querySelector('img') : null;
  }

  /* Tracks whether the user reached the case study via the open
     FLIP (i.e. clicking a cover). If false (direct URL load,
     refresh on /work/<slug>), the close path skips the reverse
     FLIP and falls back to a simple opacity fade since there's
     no source cover to land into. */
  let visitedFromPortfolio = false;

  /* Defer to the router for the actual display/visibility swap. */
  function applyState(route) {
    if (window.__applyRouteImmediate) window.__applyRouteImmediate(route);
  }

  /* ── OPEN ──────────────────────────────────────────────────── */
  window.__csFlipOpen = async function (slug, sourceImg) {
    if (!sourceImg) return applyState({ view: 'caseStudy', slug: slug });
    const sourceWrapper = sourceImg.closest('.cover-wrapper');
    if (!sourceWrapper) return applyState({ view: 'caseStudy', slug: slug });

    /* Commit: mark that the user reached the case study via the
       FLIP, so the close path knows it can play the reverse. */
    visitedFromPortfolio = true;

    /* ── MOBILE FAST PATH ──────────────────────────────────────
       The full desktop FLIP crashes the iOS WebContent process
       on /work → /work/<slug> — confirmed via bisect. The
       offending combination is (a) both #view-portfolio and
       #view-case-study mounted at once for ~520ms during
       Phase 1 + cs-content-fade, (b) an animated opacity 0→1
       on .cs-scroller while it contains backdrop-filtered
       children (iOS treats this as a complex transparency
       layer and promotes everything beneath to its own GPU
       buffer), and (c) cs-scatter applied to every
       cover-wrapper at once. This branch keeps the visual
       signature of the FLIP — the cover image flies to the
       hero rect — but cuts (a), (b), and (c) entirely. The
       clone is z 99999 so it covers the visual seam: the
       portfolio is snap-cut to display:none the moment the
       clone starts moving, and the case-study mounts under
       the clone with no content fade. When the clone arrives,
       we hand off to the real hero (instant swap). */
    if (isMobile()) {
      stopHeavyBackgrounds();

      const srcRectM = sourceImg.getBoundingClientRect();
      const dstRectM = computeHeroDstRect(srcRectM);
      const cloneM = buildClone(sourceImg, srcRectM);
      document.body.appendChild(cloneM);

      /* Hide the real hero before mounting the case-study so it
         doesn't peek out from under the clone. */
      const heroImgM = getHeroImg(slug);
      if (heroImgM) heroImgM.classList.add('cs-hidden');

      /* Snap-cut to the case-study now (no dual mount). */
      applyState({ view: 'caseStudy', slug: slug });

      /* Wait two frames for the case-study layout to settle. */
      await nextPaint();

      void cloneM.offsetWidth;
      cloneM.style.transition =
        'top '           + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
        'left '          + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
        'width '         + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
        'height '        + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
        'border-radius ' + FLIP_MS + 'ms ' + FLIP_EASE;
      cloneM.style.top    = dstRectM.top    + 'px';
      cloneM.style.left   = dstRectM.left   + 'px';
      cloneM.style.width  = dstRectM.width  + 'px';
      cloneM.style.height = dstRectM.height + 'px';

      await onceTransitionEnd(cloneM, 'top', FLIP_MS + 200);

      if (heroImgM) heroImgM.classList.remove('cs-hidden');
      cloneM.remove();
      portfolio.classList.remove('is-cs-transitioning');
      return;
    }

    /* ── DESKTOP PATH ───────────────────────────────────────── */

    /* Fade out /work overlay UI (metadata + floating back button)
       so it doesn't sit static on top of the flying cover. */
    portfolio.classList.add('is-cs-transitioning');

    /* Kick off the light-rays fade-out now, alongside the cards
       scatter. By the time __hidePortfolioForCaseStudy fires later
       (which also calls notifyLightRays(false)), the canvas has
       already faded to 0 and that second call is a safe no-op.
       Triggering here makes the fade visible against the still-
       visible portfolio backdrop instead of after the case-study
       has fully covered it. */
    if (window.__lightRaysStop) window.__lightRaysStop();

    /* ── PHASE 1 — FLIP on top of the still-visible portfolio ── */

    /* Capture the cover's on-screen rect (First position). */
    const srcRect = sourceImg.getBoundingClientRect();
    const dstRect = computeHeroDstRect(srcRect);

    /* Mount the clone over the source. */
    const clone = buildClone(sourceImg, srcRect);
    document.body.appendChild(clone);
    sourceWrapper.classList.add('cs-source');

    /* Scatter every other card uniformly: 600px down, fade out. */
    const allWrappers = Array.from(portfolio.querySelectorAll('.cover-wrapper'));
    allWrappers.forEach(function (w) {
      if (w !== sourceWrapper) w.classList.add('cs-scatter');
    });

    /* Commit layout, then start the morph to the destination. */
    void clone.offsetWidth;
    clone.style.transition =
      'top '           + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
      'left '          + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
      'width '         + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
      'height '        + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
      'border-radius ' + FLIP_MS + 'ms ' + FLIP_EASE;
    clone.style.top    = dstRect.top    + 'px';
    clone.style.left   = dstRect.left   + 'px';
    clone.style.width  = dstRect.width  + 'px';
    clone.style.height = dstRect.height + 'px';

    /* Wait for the morph. We listen for `top` since all four
       dimensions transition together; falling back at FLIP_MS+200. */
    await onceTransitionEnd(clone, 'top', FLIP_MS + 200);

    /* ── PHASE 2 — show case study ON TOP of portfolio ──────────
       The portfolio is NOT hidden yet — it stays visible behind
       the case study as a stable safety-net background, so even
       if the case-study paints with the "wrong" content for one
       frame the user sees the (identical-looking) portfolio bg,
       not a white viewport flash. Once the content fade-in
       completes we finally hide the portfolio. Z-index ensures
       #view-case-study (9500) sits above #view-portfolio (9000)
       and below the FLIP clone (99999). */

    /* The scroller starts at opacity 0; content fades in below. */
    const scroller = caseStudy.querySelector('.cs-scroller');
    if (scroller) scroller.classList.add('cs-content-fade');

    /* Disable scroll-snap and scroll-behavior temporarily.
       CSS sets `scroll-snap-type: y mandatory` and `scroll-
       behavior: smooth` on .cs-scroller. When the scroller
       becomes visible, snap can re-snap to the nearest section
       producing a visible smooth scroll animation, and smooth
       behavior can convert programmatic scrollTop sets into
       animated scrolls. We disable both for the swap and
       restore them after the clone is gone. */
    if (scroller) {
      scroller.style.scrollSnapType = 'none';
      scroller.style.scrollBehavior = 'auto';
    }

    /* Hide the real hero <img> before the swap so when the
       scroller fades in, the hero slot is empty under the clone.
       (Querying display:none DOM works fine.) */
    const heroImgPre = getHeroImg(slug);
    if (heroImgPre) heroImgPre.classList.add('cs-hidden');

    /* Show the case study WITHOUT hiding the portfolio. This sets
       display:'' and scrolls to the target slug synchronously. */
    if (window.__showCaseStudyOver) {
      window.__showCaseStudyOver(slug);
    } else {
      /* Fallback for unexpected load order. */
      caseStudy.style.display = '';
      caseStudy.dataset.slug = slug;
      if (window.__caseStudyScrollTo) window.__caseStudyScrollTo(slug, 'auto');
    }

    /* Let layout settle. */
    await nextPaint();

    /* Fade scroller content in. The real hero stays hidden under
       the clone for the duration of the fade — otherwise its
       translateY(24px → 0) makes it peek out below the clone as
       a visible "duplicate hero" sliding up. The clone is the
       hero visually until the fade completes. */
    if (scroller) scroller.classList.add('cs-content-show');

    /* After the fade completes, reveal the real hero in its final
       position, then drop the clone. */
    await sleep(FADE_MS + 20);
    if (heroImgPre) heroImgPre.classList.remove('cs-hidden');
    clone.remove();
    if (scroller) {
      scroller.classList.remove('cs-content-fade', 'cs-content-show');
      /* Restore scroll-snap and smooth scroll for normal use. */
      scroller.style.scrollSnapType = '';
      scroller.style.scrollBehavior = '';
    }

    /* NOW hide the portfolio — it's fully covered. */
    if (window.__hidePortfolioForCaseStudy) {
      window.__hidePortfolioForCaseStudy(slug);
    }

    /* Reset portfolio state for the next time it's shown. */
    sourceWrapper.classList.remove('cs-source');
    allWrappers.forEach(function (w) { w.classList.remove('cs-scatter'); });
    portfolio.classList.remove('is-cs-transitioning');
  };

  /* ── CLOSE ─────────────────────────────────────────────────── */
  /* Two-phase reverse FLIP: mirrors __csFlipOpen.
       Phase 1 (200ms) — fade case-study scroller content out;
                         build a clone over the hero.
       Phase 2 (500ms) — show portfolio under clone, pre-scatter
                         covers, read target rect, animate clone
                         back to cover, un-scatter the rest.
     If the user arrived via direct URL (visitedFromPortfolio is
     false), there are no source cover positions to land into, so
     we fall back to the simple opacity fade. */
  window.__csFlipClose = async function (slug) {
    if (!visitedFromPortfolio) {
      caseStudy.style.transition = 'opacity ' + CLOSE_MS + 'ms ease-in';
      caseStudy.style.opacity = '0';
      await sleep(CLOSE_MS + 20);
      applyState({ view: 'portfolio' });
      caseStudy.style.opacity = '';
      caseStudy.style.transition = '';
      return;
    }

    const heroImg = getHeroImg(slug);
    const targetWrapper = getCoverWrapperBySlug(slug);
    if (!heroImg || !targetWrapper) {
      applyState({ view: 'portfolio' });
      visitedFromPortfolio = false;
      return;
    }

    /* ── MOBILE FAST PATH (mirrors __csFlipOpen mobile branch) ──
       Same rationale: no dual mount, no cs-scatter, no content
       fade. The clone is the visual continuity; the portfolio
       appears under it via snap-cut, the clone morphs from hero
       rect to cover rect, then we drop it and reveal the real
       cover. */
    if (isMobile()) {
      const srcRectM = heroImg.getBoundingClientRect();
      const cloneM = buildCloneFromHero(heroImg, srcRectM);
      document.body.appendChild(cloneM);
      heroImg.classList.add('cs-hidden');

      /* Snap-cut to portfolio now (no dual mount). The clone is
         z 99999 so it covers the visual seam. */
      applyState({ view: 'portfolio' });

      /* Belt-and-suspenders: explicitly refresh the meta block from
         the active cover. There's a MutationObserver in portfolio.js
         that watches the view's style attribute, but it doesn't fire
         reliably when 'none' → '' on iOS Safari (the inline-style
         mutation can coalesce with a paint), leaving the meta block
         visually missing on return. Calling this directly here makes
         the restore deterministic. */
      if (window.__portfolioMetaRefresh) window.__portfolioMetaRefresh();

      /* Wait for the portfolio's layout to settle so we can read
         the destination cover's rect accurately. */
      await nextPaint();

      const coverImgM = getCoverImg(targetWrapper);
      const dstRectM = coverImgM
        ? coverImgM.getBoundingClientRect()
        : targetWrapper.getBoundingClientRect();

      /* Hide the real target cover while the clone is flying so
         we don't show a duplicate. */
      targetWrapper.classList.add('cs-source');

      void cloneM.offsetWidth;
      cloneM.style.transition =
        'top '           + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
        'left '          + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
        'width '         + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
        'height '        + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
        'border-radius ' + FLIP_MS + 'ms ' + FLIP_EASE;
      cloneM.style.top    = dstRectM.top    + 'px';
      cloneM.style.left   = dstRectM.left   + 'px';
      cloneM.style.width  = dstRectM.width  + 'px';
      cloneM.style.height = dstRectM.height + 'px';

      await onceTransitionEnd(cloneM, 'top', FLIP_MS + 200);

      cloneM.remove();
      targetWrapper.classList.remove('cs-source');
      heroImg.classList.remove('cs-hidden');
      visitedFromPortfolio = false;
      return;
    }

    /* ── DESKTOP PATH (unchanged) ──────────────────────────────── */

    /* ── PHASE 1 — fade content + spawn clone over hero ──────── */
    let srcRect = heroImg.getBoundingClientRect();
    const scroller = caseStudy.querySelector('.cs-scroller');

    /* If the user scrolled the case study far below the hero,
       srcRect.top may be hundreds of pixels above the viewport. The
       FLIP transition has a fixed 500ms budget, so a huge travel
       distance reads as a rushed animation. Clamp the clone's start
       position to one hero-height above the viewport — it still
       rises from off-screen, but the travel distance is bounded.
       The real heroImg is already hidden below (cs-hidden), so the
       user never sees this discontinuity. */
    const minTop = -srcRect.height;
    if (srcRect.top < minTop) {
      srcRect = { top: minTop, left: srcRect.left, width: srcRect.width, height: srcRect.height };
    }

    /* Disable scroll-snap and smooth scroll on the scroller while
       we tear down the case study; otherwise hiding/showing can
       retrigger snap animations (same fix as the open path). */
    if (scroller) {
      scroller.style.scrollSnapType = 'none';
      scroller.style.scrollBehavior = 'auto';
    }

    /* Build the hero clone at its current rect. */
    const clone = buildCloneFromHero(heroImg, srcRect);
    document.body.appendChild(clone);
    heroImg.classList.add('cs-hidden');

    /* Fade scroller content out by re-using the open's classes:
       we add both .cs-content-fade and .cs-content-show so it's
       currently at opacity 1 with the transition installed, then
       remove .cs-content-show on the next frame to fade to 0. */
    if (scroller) {
      scroller.classList.add('cs-content-fade', 'cs-content-show');
      void scroller.offsetWidth;
      scroller.classList.remove('cs-content-show');
    }

    await sleep(360);

    /* ── PHASE 2 — swap views, scatter, FLIP back ────────────── */

    /* Keep the /work overlay UI faded while cards FLIP back. */
    portfolio.classList.add('is-cs-transitioning');
    /* Freeze cover hover effects (3D lift, push-back, edge-light) so
       they don't fire if the cursor reaches the portfolio during the
       reverse FLIP. Removed after the staggered un-scatter finishes. */
    portfolio.classList.add('covers-frozen');

    /* Show portfolio (light rays start here, before the cards
       animate back, so the bg is alive when they appear). */
    if (window.__showPortfolioUnderCaseStudy) {
      window.__showPortfolioUnderCaseStudy();
    } else {
      portfolio.style.display = '';
      portfolio.classList.remove('is-inactive');
    }

    /* Pre-scatter every wrapper with NO transition, so they're
       already off-screen the moment the portfolio paints. The
       target wrapper additionally gets cs-source so its real
       cover image is hidden behind the still-flying clone. */
    const allWrappers = Array.from(portfolio.querySelectorAll('.cover-wrapper'));
    allWrappers.forEach(function (w) {
      w.style.transition = 'none';
      w.classList.add('cs-scatter');
    });
    targetWrapper.classList.add('cs-source');
    void portfolio.offsetWidth;

    /* Read the cover's natural (un-scattered) rect — this is
       where the clone needs to land. We temporarily remove
       .cs-scatter from just the target wrapper to read its real
       layout rect, then re-add it before kicking the animation
       so it stays hidden until the open un-scatter happens. */
    targetWrapper.classList.remove('cs-scatter');
    void portfolio.offsetWidth;
    const coverImg = getCoverImg(targetWrapper);
    const dstRect  = coverImg
      ? coverImg.getBoundingClientRect()
      : targetWrapper.getBoundingClientRect();
    targetWrapper.classList.add('cs-scatter');
    void portfolio.offsetWidth;

    /* Restore wrapper transitions so removing .cs-scatter below
       actually animates them home. */
    allWrappers.forEach(function (w) { w.style.transition = ''; });

    /* Hide the case study now — the clone (z 99999) is still on
       top of the portfolio; everything else inside the case
       study disappears under it. */
    if (window.__hideCaseStudyForPortfolio) {
      window.__hideCaseStudyForPortfolio();
    } else {
      caseStudy.style.display = 'none';
    }

    /* Split wrappers into back (DOM index < target = behind in
       3D stack, --base-tz more negative) and front (DOM index >
       target = in front of target). Back cards can rise up
       alongside the flying clone with no z-order conflict, since
       they paint behind it. Front cards must wait until after
       the clone is gone and the real cover is in the stack. */
    const targetIdx = allWrappers.indexOf(targetWrapper);
    const backCards  = allWrappers.filter(function (w, i) { return i <  targetIdx && w !== targetWrapper; });
    const frontCards = allWrappers.filter(function (w, i) { return i >  targetIdx && w !== targetWrapper; });

    /* Start clone morph (hero → cover slot, 500ms). */
    void clone.offsetWidth;
    clone.style.transition =
      'top '           + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
      'left '          + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
      'width '         + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
      'height '        + FLIP_MS + 'ms ' + FLIP_EASE + ', ' +
      'border-radius ' + FLIP_MS + 'ms ' + FLIP_EASE;
    clone.style.top    = dstRect.top    + 'px';
    clone.style.left   = dstRect.left   + 'px';
    clone.style.width  = dstRect.width  + 'px';
    clone.style.height = dstRect.height + 'px';

    /* Back cards rise alongside the clone — they're behind it in
       the 3D stack, so even when their paths cross visually,
       they paint behind. */
    backCards.forEach(function (w) { w.classList.remove('cs-scatter'); });

    /* Wait for clone to fully land. */
    await onceTransitionEnd(clone, 'top', FLIP_MS + 200);

    /* ── Hand-off: clone → real cover (instant, in-stack) ────────
       Remove the clone and reveal the real cover. The target
       wrapper is still .cs-scatter (translate Y 600px, opacity 0)
       and .cs-source (visibility hidden). We need it to snap
       instantly to its natural position WITHOUT a transition —
       otherwise the freshly-revealed cover would slide up from
       600px below or fade in from invisible. Suppress its
       transition for one frame, drop both classes, force reflow,
       then restore. The real cover now lives in .cover-stack at
       the target's DOM index so it renders at correct 3D depth. */
    clone.remove();
    targetWrapper.style.transition = 'none';
    targetWrapper.classList.remove('cs-source');
    targetWrapper.classList.remove('cs-scatter');
    void targetWrapper.offsetWidth;
    targetWrapper.style.transition = '';
    heroImg.classList.remove('cs-hidden');

    /* NOW un-scatter the front cards, staggered. The card closest
       to the target lifts first; each subsequent (more frontward)
       card follows ~70ms later. The .cs-scatter rule's 400ms
       transition handles the motion of each individual card. */
    frontCards.forEach(function (w, i) {
      setTimeout(function () { w.classList.remove('cs-scatter'); }, i * 70);
    });
    if (scroller) {
      scroller.classList.remove('cs-content-fade');
      scroller.style.scrollSnapType = '';
      scroller.style.scrollBehavior = '';
    }
    caseStudy.style.opacity = '';
    caseStudy.style.transition = '';
    visitedFromPortfolio = false;
    portfolio.classList.remove('is-cs-transitioning');

    /* Unfreeze cover hover after the last front card has finished its
       400ms cs-scatter transition. If the pointer is already over a
       cover, the CSS :has(:hover) rule gently animates in via the
       default 0.6s transform transition. */
    var lastCardDelay = Math.max(0, (frontCards.length - 1) * 70);
    setTimeout(function () {
      portfolio.classList.remove('covers-frozen');
    }, lastCardDelay + 420);
  };

}
